import { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_small-biz-insurance/artifacts/mi1lmpqz_Screenshot%202026-02-26%20083114.png";

const navLinks = [
  { label: "Home", path: "/" },
  { label: "About", path: "/about" },
  { label: "Services", path: "/services" },
  { label: "Reviews", path: "/reviews" },
  { label: "Resources", path: "/resources" },
  { label: "Careers", path: "/careers" },
  { label: "Contact", path: "/contact" },
];

// Sub-components
const NavLinks = ({ pathname }) => (
  <div className="hidden lg:flex items-center space-x-8">
    {navLinks.map((link) => (
      <Link
        key={link.path}
        to={link.path}
        data-testid={`nav-link-${link.label.toLowerCase()}`}
        className={`font-medium text-sm transition-colors duration-200 ${
          pathname === link.path ? "text-[#2EC4C4]" : "text-[#1A2744] hover:text-[#2EC4C4]"
        }`}
        style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
      >
        {link.label}
      </Link>
    ))}
  </div>
);

const MobileMenu = ({ pathname }) => (
  <div data-testid="mobile-menu" className="lg:hidden bg-white border-t border-gray-100 shadow-lg">
    <div className="px-4 py-4 space-y-1">
      {navLinks.map((link) => (
        <Link
          key={link.path}
          to={link.path}
          data-testid={`mobile-nav-${link.label.toLowerCase()}`}
          className={`block py-3 px-2 font-medium text-base rounded-lg transition-colors ${
            pathname === link.path ? "text-[#2EC4C4] bg-[#EAF9F9]" : "text-[#1A2744] hover:bg-gray-50"
          }`}
        >
          {link.label}
        </Link>
      ))}
      <div className="pt-3 border-t border-gray-100 space-y-2">
        <a href="tel:+17017217993" data-testid="mobile-phone" className="flex items-center space-x-2 text-[#1A2744] font-medium py-2 px-2">
          <Phone size={16} className="text-[#2EC4C4]" />
          <span>(701) 721-7993</span>
        </a>
        <Link to="/contact" data-testid="mobile-cta" className="block text-center bg-[#2EC4C4] hover:bg-[#25A0A0] text-white font-semibold rounded-lg px-6 py-3 text-sm transition-all">
          Get a Free Quote
        </Link>
      </div>
    </div>
  </div>
);

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 10);
  }, [setScrolled]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location, setMobileOpen]);

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 bg-white transition-shadow duration-300 ${scrolled ? "shadow-md" : "shadow-sm"}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <Link to="/" data-testid="navbar-logo" className="flex-shrink-0">
            <img src={LOGO_URL} alt="Viall Business Solutions" className="h-12 lg:h-14 w-auto object-contain" />
          </Link>

          <NavLinks pathname={location.pathname} />

          <div className="hidden lg:flex items-center space-x-4">
            <a href="tel:+17017217993" data-testid="navbar-phone" className="flex items-center space-x-2 text-sm font-medium text-[#1A2744] hover:text-[#2EC4C4] transition-colors">
              <Phone size={16} /><span>(701) 721-7993</span>
            </a>
            <Link to="/contact" data-testid="navbar-cta" className="bg-[#2EC4C4] hover:bg-[#25A0A0] text-white font-semibold rounded-lg px-6 py-2.5 text-sm transition-all shadow-md hover:shadow-lg active:scale-95">
              Get a Free Quote
            </Link>
          </div>

          <button
            data-testid="navbar-hamburger"
            className="lg:hidden text-[#1A2744] p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {mobileOpen && <MobileMenu pathname={location.pathname} />}
    </nav>
  );
}
